/**
 * Upload a Home button image through the launcher icon API.
 * Persists nothing except the asset id returned to the caller.
 */
import { getAuth } from 'firebase/auth';
import { fetchWithAuth } from '../../api/backendApi';
import { dealerAdminHeaders } from '../../pages/Admin/dealerAdminSession';
import { putImageToSignedUrl } from '../../utils/imageDisplayUpload';
import { backendApiUrl } from '../../config/backendApiUrl';

export const HOME_ICON_MAX_BYTES = 2 * 1024 * 1024;

const DECLARED_TYPES = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/jpg': 'image/jpeg',
  'image/webp': 'image/webp',
};

function readFileHeader(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result).subarray(0, 16));
    reader.onerror = () => reject(reader.error || new Error('Could not read image'));
    reader.readAsArrayBuffer(file.slice(0, 16));
  });
}

function sniffContentType(bytes) {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 12
    && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return '';
}

export async function readHomeIconFile(file) {
  if (!file) throw new Error('Choose a PNG, JPEG, or WebP image');
  if (file.size <= 0 || file.size > HOME_ICON_MAX_BYTES) {
    throw new Error('Image must be 2 MB or smaller');
  }
  const declared = DECLARED_TYPES[String(file.type || '').toLowerCase()] || '';
  const header = await readFileHeader(file);
  const sniffed = sniffContentType(header);
  if (!sniffed || (declared && declared !== sniffed)) {
    throw new Error('Use a PNG, JPEG, or WebP image');
  }
  return sniffed;
}

export async function uploadLauncherIcon({ file, tenantId, scope, targetKind = 'home' }) {
  const contentType = await readHomeIconFile(file);
  const kind = targetKind === 'shortcut' ? 'shortcut' : 'home';
  const created = await fetchWithAuth('/api/v2/launcher-icons', 'POST', {
    tenantId,
    scope,
    targetKind: kind,
    contentType,
    byteSize: file.size,
  });
  const assetId = created?.data?.asset?.assetId;
  const upload = created?.data?.upload;
  if (!assetId || !upload?.url) {
    throw new Error('Image upload could not be started');
  }
  await putImageToSignedUrl({
    uploadUrl: upload.url,
    headers: upload.headers || { 'Content-Type': 'application/octet-stream' },
    blob: file,
  });
  const finalized = await fetchWithAuth(
    `/api/v2/launcher-icons/${encodeURIComponent(assetId)}/finalize?tenantId=${encodeURIComponent(tenantId || '')}`,
    'POST',
    { tenantId },
  );
  if (finalized?.data?.state !== 'ready' || !finalized?.data?.assetId) {
    throw new Error('Image could not be prepared');
  }
  return finalized.data.assetId;
}

export async function fetchLauncherIconBlob({ assetId, tenantId, forceRefresh = false }) {
  const user = getAuth().currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await user.getIdToken(forceRefresh);
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  const response = await fetch(
    backendApiUrl(`/api/v2/launcher-icons/${encodeURIComponent(assetId)}/content?${params.toString()}`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        ...dealerAdminHeaders(),
      },
    },
  );
  if (response.status === 401 && !forceRefresh) {
    return fetchLauncherIconBlob({ assetId, tenantId, forceRefresh: true });
  }
  if (!response.ok) {
    throw new Error(`Icon image failed (${response.status})`);
  }
  const blob = await response.blob();
  if (!blob || blob.size === 0) throw new Error('Empty icon image');
  return blob;
}
